import React, { FC, FormEvent, Fragment, useEffect, useState } from 'react';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
} from '@mui/material';

import styles from './EditCodeDialog.module.scss';
import { WordMappingModel } from 'pages/ContextManagerPage/ContextManagementView/WordMappingModel';
import { ContextManagerService } from 'pages/ContextManagerPage/ContextManagerService';
import ConfirmationDialog from 'dialogs/ConfirmationDialog/ConfirmationDialog';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface EditCodeDialogProps {
  field: 'code_S' | 'code_SS' | 'code_COM';
  onClose: () => void;
  onCodeUpdate: () => void;
  rdsPsSystemCodes: string[] | null;
  rdsPsSubSystemCodes: string[] | null;
  rdsPsComponentCodes: string[] | null;
  wordMapping: WordMappingModel;
}

enum CodeLevel {
  code_S = 'System',
  code_SS = 'Sub System',
  code_COM = 'Component',
}

const EditCodeDialog: FC<EditCodeDialogProps> = (
  {
    field,
    onClose,
    onCodeUpdate,
    rdsPsSystemCodes,
    rdsPsSubSystemCodes,
    rdsPsComponentCodes,
    wordMapping
  }
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [dialogTitle, setDialogTitle] = useState<string>('Edit Code');
  const [formInput, setFormInput] = useState<string>('');
  const [inputLabel, setInputLabel] = useState<string>('Code');
  const [isCodeUpdateConfirmationDialogOpen, setIsCodeUpdateConfirmationDialogOpen] = useState<boolean>(false);
  const [oldCode, setOldCode] = useState<string>('');

  useEffect(() => {
    setDialogTitle(`Edit ${wordMapping.word} Code`);
    setFormInput(wordMapping[field]);
    setInputLabel(CodeLevel[field] + ' Code');
    setOldCode(wordMapping[field]);

    if (field === 'code_S') {
      if (!rdsPsSystemCodes) return;
      setCodes(rdsPsSystemCodes);
    }

    if (field === 'code_SS') {
      if (!rdsPsSubSystemCodes) return;
      setCodes(rdsPsSubSystemCodes);
    }

    if (field === 'code_COM') {
      if (!rdsPsComponentCodes) return;
      setCodes(rdsPsComponentCodes);
    }

  }, [field, rdsPsComponentCodes, rdsPsSubSystemCodes, rdsPsSystemCodes, wordMapping]);

  const cancelCodeUpdateHandler = (): void => {
    setIsCodeUpdateConfirmationDialogOpen(false);
  }

  const closeDialogHandler = (event?: Object, reason?: any): void => {
    if (reason && reason === 'backdropClick') return;

    onClose();
  };

  const confirmCodeUpdateHandler = async (): Promise<void> => {
    setIsCodeUpdateConfirmationDialogOpen(false);
    try {
      if (formInput === oldCode) return;
      if (!codes.includes(formInput)) return;

      const newWordMapping = { ...wordMapping, [field]: formInput};
      const updateResult = await ContextManagerService.updateWordMapping(newWordMapping);

      const errorDetails = updateResult?.error;
      if (!updateResult || errorDetails) {
        throw new Error('Error updating word mapping: ' + errorDetails);
      }

      onCodeUpdate();
      closeDialogHandler();
    } catch (error) {
      console.error('Failed to update word mapping:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed to update word mapping. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  }

  const formSubmitHandler = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault(); // Prevent the default form submission behavior, which sends a request
    setIsCodeUpdateConfirmationDialogOpen(true);
  }

  const formChangeHandler = (event: any, value: string): void => {
    setFormInput(value);
  };

  return (
    <Fragment>
      <ConfirmationDialog
        open={isCodeUpdateConfirmationDialogOpen}
        title={`Update ${CodeLevel[field]} Code for ${wordMapping.word} from ${oldCode} to ${formInput}`}
        message="Please confirm you want to update this code. This will be a global change, which will impact all sensors with that word when rebuilding models."
        onConfirm={confirmCodeUpdateHandler}
        onCancel={cancelCodeUpdateHandler}
      />

      <Dialog
        className={styles.EditCodeDialog}
        data-testid="EditCodeDialog"
        maxWidth={'xl'}
        open={true}
        onClose={closeDialogHandler}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <form
            className={styles.editCodeForm}
            onSubmit={formSubmitHandler}
          >
            <FormControl
              className={styles.levelInput}
              required
            >
              <Autocomplete
                options={codes}
                value={formInput}
                disableClearable
                onInputChange={(event: any, value: string) => formChangeHandler(event, value)}
                renderInput={(params) => <TextField {...params} label={inputLabel} />}
              />
            </FormControl>

            <DialogActions className={styles.formButtons}>
              <Button
                variant="contained"
                color="error"
                onClick={closeDialogHandler}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                color={'primary'}
                variant="contained"
                disabled={formInput === oldCode}
              >
                Confirm
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </Fragment>
  );
}

export default EditCodeDialog;
